import React, { useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Paper, Typography, Tabs, Tab, Select, MenuItem, IconButton } from '@mui/material';
import { ArrowBack } from "@mui/icons-material";
import Grid from '@mui/material/Grid2';
import AppContext from '../components/AppContext'; 
import config from '../config.js';
import Saldo from '../components/common/Saldo';
import ConceptList from '../components/common/ConceptList';
import ConceptHierarchyList from '../components/common/ConceptHierarchyList';
import MovimentsListEdit from '../components/common/MovimentsListEdit';
import { listAnys, listMesos } from '../functions/common-lists.js';
import DataLoader from '../components/common/DataLoader.js';
import useBack from '../hooks/useBack.js';

const DashboardMensual = () => {
  const { ctxApp, setCtxApp } = useContext(AppContext);
  const [tabValue, setTabValue] = useState(ctxApp?.dashboardMensualTabSelected || 0);
  const [viewMode, setViewMode] = useState('estalvi'); // 'estalvi' (default) -> 'inversiones' -> 'actual'

  const navigate = useNavigate();

  const [reloadId, setReloadId] = useState(0);
  const { userapp: paramUserapp, anyo: paramAnyo, mes: paramMes } = useParams();
  const userapp = paramUserapp;
  const anyo = paramAnyo;
  const mes = paramMes;
  const id = anyo + '-' + mes + '-' + reloadId;

  const { onBack } = useBack();

  const handleToggleViewMode = () => {
    setViewMode(prev => {
      if (prev === 'estalvi') return 'inversiones';
      if (prev === 'inversiones') return 'actual';
      return 'estalvi';
    });
  };

  const gotoBack = () => {
    //navigate(`/${userapp}/dashboard/${anyo}`);
    onBack();
  }; 

  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setCtxApp(prev => ({ ...prev, dashboardMensualTabSelected: newValue }));
  };    

  const handleYearMonth = (anio, mes) => {
    navigate(`/${userapp}/dashboard/${anio}/mes/${mes}`, { replace: true });
  };   

  const fetchFunction = (jwtToken, onData, onError, onFinally) => {
    axios.get(`${config.apiBaseUrl}/v1/resummensual/${userapp}/${anyo}/${mes}`, {
      headers: { Authorization: `${jwtToken}`}
    })
    .then((response) => {
      onData(response);
    })
    .catch((err) => {
      onError(err, 'cargar datos');
    })
    .finally(() => onFinally());
  };

  return (
    <DataLoader id={id} fetchFunction={fetchFunction} goBackLocation={`/${userapp}/dashboard/${anyo}`} >
      {(data, onError) => {
        // Calcular desinversiones e inversiones del total de conceptos
        let desinversiones = 0;
        let inversiones = 0;
        if (data.mapAgregatConceptes) {
          Object.values(data.mapAgregatConceptes).forEach(item => {
            if (item.concepte?.tipus === 'INVERSIO') {
              const val = item.importAgregat?.saldo || 0;
              if (val > 0) {
                desinversiones += val;
              } else {
                inversiones += val;
              }
            }
          });
        }

        // Calcular valores según el modo de vista seleccionado
        let mensualSaldo, mensualIngressos, mensualDespeses;
        let varSaldo, varIngressos, varDespeses;
        let mitjaSaldo, mitjaIngressos, mitjaDespeses;
        let desvSaldo, desvIngressos, desvDespeses;

        if (viewMode === 'estalvi') {
          mensualSaldo = data.agregat?.importAgregat?.saldoEstalvi;
          mensualIngressos = data.agregat?.importAgregat?.ingressos;
          mensualDespeses = data.agregat?.importAgregat?.despeses;

          varSaldo = data.agregat?.variacioAnyAnterior ? data.agregat.variacioAnyAnterior.saldoEstalvi : 0;
          varIngressos = data.agregat?.variacioAnyAnterior ? data.agregat.variacioAnyAnterior.ingressos : 0;
          varDespeses = data.agregat?.variacioAnyAnterior ? data.agregat.variacioAnyAnterior.despeses : 0;

          mitjaSaldo = data.agregat?.importAgregat?.mitjaSaldoEstalvi;
          mitjaIngressos = data.agregat?.importAgregat?.mitjaIngressos;
          mitjaDespeses = data.agregat?.importAgregat?.mitjaDespeses;

          desvSaldo = data.agregat?.variacioMitjaMesos ? data.agregat.variacioMitjaMesos.saldoEstalvi : 0;
          desvIngressos = data.agregat?.variacioMitjaMesos ? data.agregat.variacioMitjaMesos.ingressos : 0;
          desvDespeses = data.agregat?.variacioMitjaMesos ? data.agregat.variacioMitjaMesos.despeses : 0;
        } else if (viewMode === 'inversiones') {
          mensualSaldo = data.agregat?.importAgregat?.inversions;
          mensualIngressos = desinversiones;
          mensualDespeses = inversiones;

          varSaldo = data.agregat?.variacioAnyAnterior ? data.agregat.variacioAnyAnterior.inversions : 0;
          varIngressos = undefined;
          varDespeses = undefined;

          mitjaSaldo = data.agregat?.importAgregat?.mitjaInversions;
          mitjaIngressos = undefined;
          mitjaDespeses = undefined;

          desvSaldo = data.agregat?.variacioMitjaMesos ? data.agregat.variacioMitjaMesos.inversions : 0;
          desvIngressos = undefined;
          desvDespeses = undefined;
        } else { // actual
          mensualSaldo = data.agregat?.importAgregat?.saldo;
          mensualIngressos = (data.agregat?.importAgregat?.ingressos || 0) + desinversiones;
          mensualDespeses = (data.agregat?.importAgregat?.despeses || 0) + inversiones;

          varSaldo = data.agregat?.variacioAnyAnterior ? data.agregat.variacioAnyAnterior.saldo : 0;
          varIngressos = undefined;
          varDespeses = undefined;

          mitjaSaldo = data.agregat?.importAgregat?.mitjaSaldo;
          mitjaIngressos = undefined;
          mitjaDespeses = undefined;

          desvSaldo = data.agregat?.variacioMitjaMesos ? data.agregat.variacioMitjaMesos.saldo : 0;
          desvIngressos = undefined;
          desvDespeses = undefined;
        }

        return (
        <>
          <Grid 
            size={{xs: 12, sm: 6 }} 
            sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}
          >
            <IconButton onClick={()=>gotoBack()}>
              <ArrowBack />
            </IconButton>

            <Typography variant="h5">
              Dashboard Mensual
            </Typography>
            <Select
              value={data.anio}
              label="Año"
              onChange={(e) => handleYearMonth(e.target.value, data.mes)}
            >
              {listAnys().map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
            <Select
              value={data.mes}
              label="Mes"
              onChange={(e) => handleYearMonth(data.anio, e.target.value)}
            >
              {listMesos.map((mes) => (
                <MenuItem key={mes.key} value={mes.key}>
                  {mes.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid container spacing={2} sx={{ marginTop: '10px' }}>
            <Saldo titol="Saldo Mensual" 
              saldo={mensualSaldo} 
              ingressos={mensualIngressos}
              despeses={mensualDespeses}
              mode={viewMode}
              onToggleMode={handleToggleViewMode}
              showToggleIcon={true} />

            <Saldo titol="Dif. any anterior" 
              saldo={varSaldo} 
              ingressos={varIngressos}
              despeses={varDespeses}
              mode={viewMode}
              onToggleMode={handleToggleViewMode}
              showToggleIcon={true} />

            <Saldo titol="Mitja Diari" 
              saldo={mitjaSaldo} 
              ingressos={mitjaIngressos}
              despeses={mitjaDespeses}
              mode={viewMode}
              onToggleMode={handleToggleViewMode}
              showToggleIcon={true} />

            <Saldo titol="Desviació Mitja Mensual" 
              saldo={desvSaldo} 
              ingressos={desvIngressos}
              despeses={desvDespeses}
              mode={viewMode}
              onToggleMode={handleToggleViewMode}
              showToggleIcon={true} />
          </Grid>

          <Grid container spacing={2} sx={{ marginTop: '10px' }}>
            {/* Tabbar */}
            <Grid size={12}>
              <Paper>
                <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" centered>
                  <Tab label="Conceptes" />
                  <Tab label="Conceptes (Jeràrquic)" />
                  <Tab label={`Moviments (${data.listConceptesMoviment?.length})`} />
                </Tabs>

                {tabValue === 0 && (
                  <ConceptList concepts={data.mapAgregatConceptes} tipus="mensual" viewMode={viewMode} />
                )}
                {tabValue === 1 && (
                  <ConceptHierarchyList
                    userapp={userapp}
                    anyo={data.anio}
                    mes={data.mes}
                    tipus="mensual"
                    viewMode={viewMode}
                  />
                )}
                {tabValue === 2 && (
                  <MovimentsListEdit 
                    moviments={data.listConceptesMoviment} 
                    onError={onError} 
                    onEditComplete={() => setReloadId(prev => prev + 1)}
                  />
                )}
              </Paper>
            </Grid>
          </Grid>
      </>
      );
    }}
    </DataLoader>
  );
};

export default DashboardMensual;
